/**
 * Centralized Error Handler
 * Provides consistent error handling across the application
 * 
 * Features:
 * - Categorizes errors (network, auth, server, validation, unknown)
 * - Generates user-friendly error messages
 * - Logs errors with context for debugging
 * - Determines if errors are recoverable
 */

class ErrorHandler {
  /**
   * Handle an error and return structured error information
   * 
   * @param {Error|Object} error - The error object
   * @param {Object} context - Additional context about where the error occurred
   * @param {string} context.component - Component/service name (e.g., 'ApiService', 'ChatbotPane')
   * @param {string} context.action - Action being performed (e.g., 'sendMessage', 'getProjects')
   * @param {string} context.endpoint - API endpoint (e.g., '/api/chat', '/api/projects')
   * @param {Object} context.metadata - Additional metadata
   * @returns {Object} Handled error with message, code, and recovery info
   */
  static handle(error, context = {}) {
    const {
      component = 'Unknown',
      action = 'Unknown',
      endpoint = null,
      metadata = {}
    } = context;

    // Extract error information
    const errorMessage = error?.message || String(error) || 'Unknown error';
    const errorName = error?.name || 'Error';
    const errorResponse = error?.response;
    const errorStatus = errorResponse?.status;
    const errorData = errorResponse?.data;

    // Categorize error
    const category = this._categorizeError(error, errorStatus, errorMessage);
    
    // Generate user-friendly message
    const userMessage = this._getUserMessage(category, errorMessage, errorStatus, errorData);
    
    // Generate error code
    const errorCode = this._getErrorCode(category, errorStatus, errorName);
    
    // Determine if recoverable
    const recoverable = this._isRecoverable(category, errorStatus);
    
    // Log error with context (only in development)
    if (process.env.NODE_ENV === 'development') {
      this._logError(error, category, context, {
        errorMessage,
        errorName,
        errorStatus,
        errorCode,
        userMessage
      });
    }

    // Return structured error information
    return {
      message: userMessage,
      code: errorCode,
      category,
      recoverable,
      originalError: error,
      context: {
        component,
        action,
        endpoint,
        ...metadata
      }
    };
  }

  /**
   * Categorize error type
   */
  static _categorizeError(error, status, message) {
    const messageLower = message?.toLowerCase() || '';
    
    // Network errors
    if (
      error?.name === 'NetworkError' ||
      error?.name === 'TypeError' && messageLower.includes('network') ||
      messageLower.includes('network') ||
      messageLower.includes('connection') ||
      messageLower.includes('fetch') ||
      messageLower.includes('timeout') ||
      error?.code === 'ECONNREFUSED' ||
      error?.code === 'ENOTFOUND'
    ) {
      return 'network';
    }

    // Authentication errors
    if (
      status === 401 ||
      status === 403 ||
      messageLower.includes('unauthorized') ||
      messageLower.includes('forbidden') ||
      messageLower.includes('authentication') ||
      messageLower.includes('token') && (messageLower.includes('invalid') || messageLower.includes('expired'))
    ) {
      return 'auth';
    }

    // Validation errors
    if (
      status === 400 ||
      status === 422 ||
      messageLower.includes('validation') ||
      messageLower.includes('invalid') && !messageLower.includes('token')
    ) {
      return 'validation';
    }

    // Server errors
    if (
      status >= 500 ||
      status === 503 ||
      messageLower.includes('server error') ||
      messageLower.includes('internal error')
    ) {
      return 'server';
    }

    // Not found errors
    if (status === 404 || messageLower.includes('not found')) {
      return 'not_found';
    }

    // Rate limiting
    if (status === 429 || messageLower.includes('rate limit')) {
      return 'rate_limit';
    }

    // Unknown
    return 'unknown';
  }

  /**
   * Get user-friendly error message
   */
  static _getUserMessage(category, originalMessage, status, errorData) {
    // Try to get message from error data first
    if (errorData?.message && typeof errorData.message === 'string') {
      // If it's already user-friendly, use it
      if (!errorData.message.includes('Error:') && !errorData.message.includes('Exception')) {
        return errorData.message;
      }
    }

    // Category-based messages
    switch (category) {
      case 'network':
        return 'Network connection failed. Please check your internet connection and try again.';
      
      case 'auth':
        return 'Authentication failed. Please log in again.';
      
      case 'validation':
        return errorData?.message || 'Invalid request. Please check your input and try again.';
      
      case 'server':
        return 'Server error occurred. Please try again in a moment.';
      
      case 'not_found':
        return 'The requested resource was not found.';
      
      case 'rate_limit':
        return 'Too many requests. Please wait a moment and try again.';
      
      case 'unknown':
      default:
        // For unknown errors, try to extract meaningful message
        if (originalMessage && !originalMessage.includes('Error:') && originalMessage.length < 100) {
          return originalMessage;
        }
        return 'An unexpected error occurred. Please try again.';
    }
  }

  /**
   * Generate error code for programmatic handling
   */
  static _getErrorCode(category, status, errorName) {
    if (status) {
      return `HTTP_${status}`;
    }
    
    switch (category) {
      case 'network':
        return 'NETWORK_ERROR';
      case 'auth':
        return 'AUTH_ERROR';
      case 'validation':
        return 'VALIDATION_ERROR';
      case 'server':
        return 'SERVER_ERROR';
      case 'not_found':
        return 'NOT_FOUND';
      case 'rate_limit':
        return 'RATE_LIMIT';
      default:
        return errorName?.toUpperCase() || 'UNKNOWN_ERROR';
    }
  }

  /**
   * Determine if error is recoverable (user can retry)
   */
  static _isRecoverable(category, status) {
    // Network errors are usually recoverable
    if (category === 'network') {
      return true;
    }
    
    // Server errors (5xx) are usually recoverable
    if (status >= 500 && status < 600) {
      return true;
    }
    
    // Rate limiting is recoverable after waiting
    if (category === 'rate_limit') {
      return true;
    }
    
    // Auth errors need re-authentication
    if (category === 'auth') {
      return false;
    }
    
    // Validation errors need user to fix input
    if (category === 'validation') {
      return false;
    }
    
    // Not found errors are usually not recoverable
    if (category === 'not_found') {
      return false;
    }
    
    // Unknown errors - assume not recoverable to be safe
    return false;
  }

  /**
   * Log error with context (development only)
   */
  static _logError(error, category, context, errorInfo) {
    const logData = {
      timestamp: new Date().toISOString(),
      category,
      ...errorInfo,
      context: {
        component: context.component,
        action: context.action,
        endpoint: context.endpoint,
        ...context.metadata
      }
    };

    // Use appropriate log level
    if (category === 'server' || category === 'unknown') {
      console.error('❌ [ErrorHandler]', logData);
    } else if (category === 'network' || category === 'auth') {
      console.warn('⚠️ [ErrorHandler]', logData);
    } else {
      console.log('ℹ️ [ErrorHandler]', logData);
    }

    // Log full error stack in development
    if (error?.stack && process.env.NODE_ENV === 'development') {
      console.error('Error stack:', error.stack);
    }
  }

  /**
   * Get user message (convenience method)
   */
  static getUserMessage(error, context = {}) {
    return this.handle(error, context).message;
  }

  /**
   * Get error code (convenience method)
   */
  static getErrorCode(error, context = {}) {
    return this.handle(error, context).code;
  }

  /**
   * Check if error is recoverable (convenience method)
   */
  static isRecoverable(error, context = {}) {
    return this.handle(error, context).recoverable;
  }
}

export default ErrorHandler;

